import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import {
    getFirestore,
    collection,
    addDoc,
    doc,
    getDocs,
    getDoc,
    Timestamp,
    query,
    where
} from "firebase/firestore";
import { app } from "../firebase";
import { useNavigate } from "react-router-dom";

// === Animations ===
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

// === Styled Components ===

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2.5rem;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 50px rgba(0,0,0,0.15);
  animation: ${slideUp} 0.3s ease-out;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #bbb;
  }
`;

const Fechar = styled.button`
  position: absolute;
  top: 20px;
  right: 25px;
  font-size: 28px;
  color: #888;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #1d1d1d;
  }
`;

const Header = styled.div`
    margin-bottom: 2rem;
    text-align: center;
`;

const Title = styled.h2`
  margin: 0;
  color: #1d1d1d;
  font-size: 1.8rem;
`;

const Subtitle = styled.p`
    color: #666;
    margin-top: 5px;
    font-size: 0.95rem;
`;

const Section = styled.section`
  margin-top: 2rem;
  animation: ${fadeIn} 0.4s ease-out forwards;
  
  h4 {
    color: #333;
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 8px;
    
    &::before {
        content: '';
        display: block;
        width: 4px;
        height: 18px;
        background-color: ${props => props.color || "#3a79ed"};
        border-radius: 2px;
    }
  }
`;

const GridContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 12px;
`;

const ServiceCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  padding: 15px;
  border: 2px solid ${(props) => (props.active ? props.color : "#eee")};
  background-color: ${(props) => (props.active ? `${props.color}0D` : "white")};
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 80px;
  text-align: left;

  &:hover {
    border-color: ${(props) => props.color};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  }

  strong {
    color: ${(props) => (props.active ? props.color : "#333")};
    font-size: 0.95rem;
    margin-bottom: 4px;
  }

  span {
    font-size: 0.85rem;
    color: #666;
    font-weight: 500;
  }
  
  .price {
      font-weight: bold;
      color: ${(props) => props.color};
      margin-top: 8px;
  }
`;

const DayButton = styled.button`
  background-color: ${(props) => (props.active ? props.color : "transparent")};
  border: 1px solid ${(props) => (props.active ? props.color : "#ddd")};
  border-radius: 12px;
  padding: 10px;
  min-width: 70px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: ${(props) => props.color};
  }

  span {
      font-size: 0.8rem;
      text-transform: uppercase;
      color: ${(props) => (props.active ? "white" : "#888")};
      margin-bottom: 4px;
  }

  strong {
      font-size: 1.2rem;
      color: ${(props) => (props.active ? "white" : "#333")};
  }
`;

const BarberCard = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px 20px;
    border: 2px solid ${(props) => (props.active ? props.color : "#eee")};
    background-color: ${(props) => (props.active ? props.color : "white")};
    color: ${(props) => (props.active ? "white" : "#555")};
    border-radius: 50px;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.2s;

    &:hover {
        border-color: ${(props) => props.color};
        color: ${(props) => (props.active ? "white" : props.color)};
    }
`;

const HourGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`;

const HourButton = styled.button`
  background-color: ${(props) => (props.active ? props.color : "transparent")};
  border: 1px solid ${(props) => (props.active ? props.color : "#ddd")};
  border-radius: 8px;
  padding: 8px 16px;
  color: ${(props) => (props.active ? "white" : "#555")};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: 0.2s;

  &:hover {
     border-color: ${(props) => props.color};
     color: ${(props) => (props.active ? "white" : props.color)};
  }
`;

const FormRow = styled.div`
    display: flex;
    gap: 15px;
    margin-bottom: 20px;
    
    @media(max-width: 600px) {
        flex-direction: column;
    }
`;

const InputGroup = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;

    label {
        font-size: 0.85rem;
        font-weight: 600;
        color: #555;
        margin-left: 4px;
    }
`;

const InputCliente = styled.input`
  padding: 14px 16px;
  border-radius: 12px;
  border: 2px solid #eee;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s;
  width: 100%;
  background: #fcfcfc;
  color: #1d1d1d;

  &:focus {
    border-color: #3a79ed;
    background: white;
    box-shadow: 0 0 0 4px rgba(58, 121, 237, 0.1);
  }

  &::placeholder {
    color: #bbb;
  }
`;

const ConfirmButton = styled.button`
    width: 100%;
    padding: 16px;
    background-color: ${props => props.color || '#3a79ed'};
    color: white;
    font-weight: bold;
    font-size: 1.1rem;
    border: none;
    border-radius: 16px;
    cursor: pointer;
    transition: transform 0.1s, filter 0.2s;
    box-shadow: 0 4px 15px rgba(58, 121, 237, 0.3);

    &:hover {
        filter: brightness(1.1);
        transform: translateY(-1px);
    }
    
    &:active {
        transform: translateY(1px);
    }
`;

const SuccessWrapper = styled.div`
  background-color: white;
  padding: 3rem;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 10px 40px rgba(0,0,0,0.2);
  animation: ${slideUp} 0.3s ease-out;

  svg {
    margin-bottom: 1.5rem;
  }
  
  h3 {
      color: #1d1d1d;
      margin: 0;
      font-size: 1.5rem;
  }
  
  p {
      color: #666;
      margin-top: 10px;
  }
`;

const EmptyState = styled.div`
    padding: 20px;
    text-align: center;
    color: #888;
    background: #f9f9f9;
    border-radius: 12px;
    font-style: italic;
`;


// === Componente ===

export default function ModalAgendar({ barbeariaId, onClose }) {
    const primaryColor = "#3a79ed";

    const [servicos, setServicos] = useState([]);
    const [barbeiros, setBarbeiros] = useState([]);
    const [dias, setDias] = useState([]);
    const [horarios, setHorarios] = useState([]);

    const [servicoSelecionado, setServicoSelecionado] = useState(null);
    const [diaSelecionado, setDiaSelecionado] = useState(null);
    const [barbeiroSelecionado, setBarbeiroSelecionado] = useState(null);
    const [horarioSelecionado, setHorarioSelecionado] = useState(null);

    const [clienteNome, setClienteNome] = useState("");
    const [clienteTelefone, setClienteTelefone] = useState("");

    const [agendamentoSucesso, setAgendamentoSucesso] = useState(false);

    const db = getFirestore(app);
    const navigate = useNavigate();

    // === FUNÇÃO DA MÁSCARA ===
    const handlePhoneChange = (e) => {
        let value = e.target.value;

        // 1. Remove tudo que não é número
        value = value.replace(/\D/g, "");

        // 2. Limita a 11 números
        if (value.length > 11) {
            value = value.slice(0, 11);
        }

        // 3. Aplica a máscara (XX) XXXXX-XXXX
        // Coloca parênteses em volta dos dois primeiros dígitos
        value = value.replace(/^(\d{2})(\d)/g, "($1) $2");
        // Coloca hífen entre o quinto e o sexto dígitos
        value = value.replace(/(\d)(\d{4})$/, "$1-$2");

        setClienteTelefone(value);
    };

    useEffect(() => {
        const carregarDados = async () => {
            const servicosSnap = await getDocs(collection(db, `barbearia/${barbeariaId}/servico`));
            const barbeirosSnap = await getDocs(
                collection(db, `barbearia/${barbeariaId}/barbeiros`)
            );

            setBarbeiros(
                barbeirosSnap.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(b => b.status !== "inativo")
            );
            setServicos(servicosSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

            const hoje = new Date();
            // TRECHO CORRIGIDO
            const diasGerados = Array.from({ length: 7 }).map((_, i) => {
                const data = new Date();
                data.setDate(hoje.getDate() + i);

                // Correção: Montar a string YYYY-MM-DD manualmente usando horário local
                const ano = data.getFullYear();
                const mes = String(data.getMonth() + 1).padStart(2, '0');
                const dia = String(data.getDate()).padStart(2, '0');
                const dataFormatada = `${ano}-${mes}-${dia}`;

                return {
                    dataISO: dataFormatada,
                    diaNumero: data.getDate(),
                    diaSemana: data.toLocaleDateString("pt-BR", { weekday: "short" }).replace('.', ''),
                };
            });
            setDias(diasGerados);
        };

        carregarDados();
    }, [barbeariaId]);

    useEffect(() => {
        const carregarHorarios = async () => {
            if (!servicoSelecionado || !diaSelecionado || !barbeiroSelecionado) {
                setHorarios([]);
                return;
            }

            const duracaoServico = Number(servicoSelecionado.tempo);
            if (!duracaoServico) return;

            const timeToMinutes = (time) => {
                const [h, m] = time.split(":").map(Number);
                return h * 60 + m;
            };

            const semanaMap = {
                0: "domingo", 1: "segunda", 2: "terca",
                3: "quarta", 4: "quinta", 5: "sexta", 6: "sabado",
            };

            const dataObj = new Date(`${diaSelecionado.dataISO}T00:00:00`);
            const diaSemana = semanaMap[dataObj.getDay()];

            try {
                let horarioInicioStr = "";
                let horarioTerminoStr = "";

                const barbeiroHorarioRef = doc(db, `barbearia/${barbeariaId}/barbeiros/${barbeiroSelecionado.id}/horarioFuncionamento/${diaSemana}`);
                const barbeiroSnap = await getDoc(barbeiroHorarioRef);

                if (barbeiroSnap.exists() && barbeiroSnap.data().aberto) {
                    horarioInicioStr = barbeiroSnap.data().horarioInicio;
                    horarioTerminoStr = barbeiroSnap.data().horarioTermino;
                } else {
                    const geralRef = doc(db, `barbearia/${barbeariaId}/horarioFuncionamento/${diaSemana}`);
                    const geralSnap = await getDoc(geralRef);

                    if (!geralSnap.exists() || geralSnap.data().aberto === false) {
                        setHorarios([]);
                        return;
                    }
                    horarioInicioStr = geralSnap.data().horarioInicio;
                    horarioTerminoStr = geralSnap.data().horarioTermino;
                }

                if (!horarioInicioStr || !horarioTerminoStr) {
                    setHorarios([]);
                    return;
                }

                const slots = [];
                let current = new Date(`2000-01-01T${horarioInicioStr}:00`);
                const end = new Date(`2000-01-01T${horarioTerminoStr}:00`);

                while (true) {
                    const fimDoServico = new Date(current.getTime() + duracaoServico * 60000);
                    if (fimDoServico > end) break;
                    slots.push(current.toTimeString().slice(0, 5));
                    current = new Date(current.getTime() + 30 * 60000);
                }

                const q = query(
                    collection(db, `barbearia/${barbeariaId}/agendamentos`),
                    where("data", "==", diaSelecionado.dataISO),
                    where("barbeiroId", "==", barbeiroSelecionado.id)
                );

                const querySnapshot = await getDocs(q);
                const agendamentosOcupados = querySnapshot.docs.map(doc => ({
                    inicio: timeToMinutes(doc.data().horaInicio),
                    fim: timeToMinutes(doc.data().horaInicio) + Number(doc.data().duracao)
                }));

                const slotsLivres = slots.filter(slot => {
                    const slotInicio = timeToMinutes(slot);
                    const slotFim = slotInicio + duracaoServico;
                    const temColisao = agendamentosOcupados.some(agendamento => {
                        return (slotInicio < agendamento.fim && slotFim > agendamento.inicio);
                    });
                    return !temColisao;
                });

                setHorarios(slotsLivres);

            } catch (error) {
                console.error("Erro ao carregar horários:", error);
            }
        };

        carregarHorarios();
    }, [servicoSelecionado, diaSelecionado, barbeiroSelecionado, barbeariaId, db]);


    const confirmarAgendamento = async () => {
        if (!clienteNome || !clienteTelefone || clienteTelefone.length < 14) {
            alert("Preencha o nome e um telefone válido.");
            return;
        }

        try {
            await addDoc(
                collection(db, `barbearia/${barbeariaId}/agendamentos`),
                {
                    servicoId: servicoSelecionado.id,
                    servicoNome: servicoSelecionado.nome,
                    servicoValor: servicoSelecionado.valor || 0,
                    barbeiroId: barbeiroSelecionado.id,
                    barbeiroNome: barbeiroSelecionado.nome,
                    data: diaSelecionado.dataISO,
                    horaInicio: horarioSelecionado,
                    duracao: servicoSelecionado.tempo,
                    clienteNome,
                    clienteTelefone,
                    criadoEm: Timestamp.now(),
                    criadoPeloProprietario: true,
                    status: "confirmado"
                }
            );

            setAgendamentoSucesso(true);

            setTimeout(() => {
                setAgendamentoSucesso(false);
                onClose();
                navigate(0);
            }, 2500);

        } catch (error) {
            console.error("Erro ao confirmar agendamento:", error);
            alert("Ocorreu um erro ao confirmar o agendamento. Tente novamente.");
        }
    };


    return (
        <Overlay>

            {agendamentoSucesso && (
                <div style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", justifyContent: "center", alignItems: "center", background: "rgba(0,0,0,0.5)", backdropFilter: "blur(5px)" }}>
                    <SuccessWrapper>
                        <svg width="80" height="80" viewBox="0 0 120 120">
                            <circle cx="60" cy="60" r="50" stroke={primaryColor} strokeWidth="8" fill="none" />
                            <path d="M35 60 L52 77 L85 43" fill="none" stroke={primaryColor} strokeWidth="8" strokeLinecap="round" strokeLinejoin="round">
                                <animate attributeName="stroke-dasharray" from="0,100" to="100,0" dur="0.4s" fill="freeze" />
                            </path>
                        </svg>
                        <h3>Sucesso!</h3>
                        <p>O agendamento foi realizado.</p>
                    </SuccessWrapper>
                </div>
            )}


            <ModalContent>
                <Fechar onClick={onClose}>&times;</Fechar>

                <Header>
                    <Title>Novo Agendamento</Title>
                    <Subtitle>Preencha os detalhes abaixo</Subtitle>
                </Header>

                <Section color={primaryColor}>
                    <h4>1. Escolha o serviço</h4>
                    <GridContainer>
                        {servicos.map((s) => (
                            <ServiceCard
                                key={s.id}
                                color={primaryColor}
                                onClick={() => setServicoSelecionado(s)}
                                active={servicoSelecionado?.id === s.id}
                            >
                                <strong>{s.nome}</strong>
                                <span>{s.tempo} min</span>
                                {s.valor && (
                                    <span className="price">
                                        R$ {Number(s.valor).toFixed(2).replace('.', ',')}
                                    </span>
                                )}
                            </ServiceCard>
                        ))}
                    </GridContainer>
                </Section>

                {servicoSelecionado && (
                    <Section color={primaryColor}>
                        <h4>2. Escolha o dia</h4>
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
                            {dias.map((d) => (
                                <DayButton
                                    key={d.dataISO}
                                    active={diaSelecionado?.dataISO === d.dataISO}
                                    onClick={() => setDiaSelecionado(d)}
                                    color={primaryColor}
                                >
                                    <span>{d.diaSemana}</span>
                                    <strong>{d.diaNumero}</strong>
                                </DayButton>
                            ))}
                        </div>
                    </Section>
                )}

                {diaSelecionado && (
                    <Section color={primaryColor}>
                        <h4>3. Escolha o profissional</h4>
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            {barbeiros.map((b) => (
                                <BarberCard
                                    key={b.id}
                                    onClick={() => setBarbeiroSelecionado(b)}
                                    color={primaryColor}
                                    active={barbeiroSelecionado?.id === b.id}
                                >
                                    {b.nome}
                                </BarberCard>
                            ))}
                        </div>
                    </Section>
                )}

                {barbeiroSelecionado && (
                    <Section color={primaryColor}>
                        <h4>4. Horário disponível</h4>
                        {horarios.length > 0 ? (
                            <HourGrid>
                                {horarios.map((h) => (
                                    <HourButton
                                        key={h}
                                        active={horarioSelecionado === h}
                                        onClick={() => setHorarioSelecionado(h)}
                                        color={primaryColor}
                                    >
                                        {h}
                                    </HourButton>
                                ))}
                            </HourGrid>
                        ) : (
                            <EmptyState>Nenhum horário disponível para esta data/profissional.</EmptyState>
                        )}
                    </Section>
                )}

                {horarioSelecionado && (
                    <>
                        <Section color={primaryColor}>
                            <h4>5. Dados do cliente</h4>
                            <FormRow>
                                <InputGroup>
                                    <label>Nome do Cliente</label>
                                    <InputCliente
                                        type="text"
                                        placeholder="Ex: João Silva"
                                        value={clienteNome}
                                        onChange={(e) => setClienteNome(e.target.value)}
                                    />
                                </InputGroup>
                                <InputGroup>
                                    <label>WhatsApp / Telefone</label>
                                    {/* MÁSCARA APLICADA AQUI */}
                                    <InputCliente
                                        type="tel"
                                        placeholder="(00) 00000-0000"
                                        value={clienteTelefone}
                                        onChange={handlePhoneChange}
                                        maxLength={15}
                                    />
                                </InputGroup>
                            </FormRow>
                        </Section>

                        <Section style={{ marginTop: '30px' }}>
                            <ConfirmButton color={primaryColor} onClick={confirmarAgendamento}>
                                Confirmar Agendamento
                            </ConfirmButton>
                        </Section>
                    </>
                )}
            </ModalContent>
        </Overlay>
    );
}